import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { FileText, Upload, X, Loader2, CheckCircle2 } from 'lucide-react';
import { compressImage } from '../../lib/photos';
import { formatApiError } from '../../api';

const MAX_BYTES = 5 * 1024 * 1024;

// Picks a KYC document (Aadhaar card / driving license) — image gets downscaled
// to a legible resolution, PDF is passed through as-is.
// Two modes: `onFile(file)` holds the file for the parent to upload later (registration
// flow); or pass `upload`+`onUploaded` to upload immediately on selection (dashboard replace flow).
export const DocumentPicker = ({
  label, hint = 'JPG, PNG or PDF · max 5MB', onFile, upload, onUploaded, alreadyUploaded = false, testId,
}) => {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleChange = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    setError('');
    setBusy(true);
    try {
      let file = picked;
      let previewUrl = null;
      if (picked.type.startsWith('image/')) {
        file = await compressImage(picked, 1600, 0.85);
        previewUrl = URL.createObjectURL(file);
      } else if (picked.type === 'application/pdf') {
        if (picked.size > MAX_BYTES) throw new Error('File must be smaller than 5 MB');
      } else {
        throw new Error('Please upload a JPG, PNG or PDF file');
      }
      setPreview(previewUrl);
      setFileName(picked.name);
      onFile?.(file);
      if (upload) {
        const result = await upload(file);
        onUploaded?.(result);
        // Upload succeeded — collapse back to the reusable "already uploaded" chip
        // (driven by the parent's alreadyUploaded prop) instead of holding onto this
        // session's local filename/preview indefinitely.
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreview(null);
        setFileName('');
      }
    } catch (err) {
      setError(err?.response ? formatApiError(err) : err?.message || 'Could not use this file');
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFileName('');
    setPreview(null);
    onFile?.(null);
  };

  const showAlreadyUploadedChip = !fileName && alreadyUploaded;

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={handleChange}
        data-testid={`${testId}-input`}
      />
      {!fileName && !showAlreadyUploadedChip && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full h-28 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center gap-1.5 hover:border-accent hover:bg-accent/5 transition-colors"
          data-testid={`${testId}-btn`}
        >
          {busy ? (
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          ) : (
            <Upload className="w-6 h-6 text-muted-foreground" />
          )}
          <span className="text-sm font-medium text-foreground">{label}</span>
          <span className="text-xs text-muted-foreground">{hint}</span>
        </button>
      )}

      {showAlreadyUploadedChip && (
        <div className="flex items-center gap-3 border border-border rounded-lg p-3 bg-background/60" data-testid={`${testId}-uploaded`}>
          <div className="w-10 h-10 rounded-md bg-green-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-green-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">{label}</p>
            <p className="text-xs text-green-700">Uploaded and on file</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testId}-btn`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Replace'}
          </Button>
        </div>
      )}

      {fileName && (
        <div className="flex items-center gap-3 border border-border rounded-lg p-3 bg-background/60">
          {preview ? (
            <img src={preview} alt={label} className="w-14 h-14 object-cover rounded-md flex-shrink-0" data-testid={`${testId}-preview`} />
          ) : (
            <div className="w-14 h-14 rounded-md bg-accent/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-accent-dark" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{fileName}</p>
            <p className="text-xs text-green-700">{busy ? 'Uploading…' : upload ? 'Uploaded and on file' : 'Ready to submit'}</p>
          </div>
          {!upload && (
            <Button type="button" variant="ghost" size="icon" onClick={clear} data-testid={`${testId}-remove`}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
      {error && <p className="text-xs text-red-600 mt-1" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
};

export default DocumentPicker;

